<template>
  <div
    :class="['menu-list__item', { 'menu-list__item--favorite': isFavorite }]"
    @click="openItem"
  >
    <div
      v-if="item.isNewItem"
      class="menu-list__item-new-banner"
    >
      New!
    </div>

    <div
      v-if="item.isLowInventory"
      class="menu-list__item-low-stock-banner"
    >
      Low Stock
    </div>

    <div
      v-if="imageUrl"
      class="menu-list__item-image"
      :style="`background-image: url(${imageUrl})`"
    />

    <div class="menu-list__item-content">
      <div class="menu-list__item-header">
        <p class="title title--6 dark no-wrap-ellipsis">
          {{ item.displayName }}
        </p>

        <p
          v-show="selectedLocation.storeId && !selectedLocation.features.HidePrices"
          class="title title--6 primary no-wrap"
        >
          {{ $dollars(item.pricePerUnitAfterMods || item.price) }}
        </p>
      </div>

      <div class="menu-list__item-description">
        <div class="menu-list__item-description-text">
          <p
            v-if="item.descriptionText"
            :class="['smaller', { 'no-wrap-ellipsis-multi-line': isDescriptionTruncated }]"
          >
            {{ item.descriptionText }}
          </p>

          <p
            v-if="isReadOnly && item.modifierSummary"
            class="smaller"
          >
            {{ item.modifierSummary }}
          </p>
        </div>

        <template v-if="!isReadOnly">
          <div class="menu-list__item-actions">
            <favorite-button
              v-if="isFavorite"
              disable-state-update
              :item="item"
              :modifiers="item.modifiers"
              :special-instructions="item.specialInstructions"
            />

            <button
              v-if="canAddItemDirectly || (isUpsellItem && itemQuantity > 0)"
              :class="['button button--icon button--small button--primary', { 'button--loading': isAddingItem, 'visible-overflow': isUpsellItem }]"
              type="button"
              title="Add to Cart"
              :disabled="order.isAddingItem"
              @click.stop="addItemToOrder"
            >
              <span class="icon far fa-shopping-bag" />
              <span class="icon icon--overlap icon--smallest fa-plus" />
              <span class="ada-text">Add to Cart</span>

              <transition
                v-show="!isAddingItem"
                name="cart-icon"
              >
                <span
                  v-if="isUpsellItem && itemQuantity > 0"
                  :key="itemQuantity"
                  class="cart-icon__count"
                >
                  {{ itemQuantity }}
                </span>
              </transition>
            </button>

            <button
              v-else
              class="button button--icon button--small button--primary"
              type="button"
              title="View Details"
              :disabled="isAddingItem"
              @click.stop="openItem"
            >
              <span class="icon far fa-plus" />
              <span class="ada-text">View Details</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  export default {
    name: 'MenuListItem',

    props: {
      categoryId: {
        type: Number,
        default: undefined
      },

      isFavorite: {
        type: Boolean,
        default: false
      },

      item: {
        type: Object,
        required: true
      },

      isReadOnly: {
        type: Boolean,
        default: false
      },

      isUpsellItem: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        isAddingItem: false
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('menu', ['menu', 'selectedCategory']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('session', ['session']),

      canAddItemDirectly() {
        return this.selectedLocation.isReadyForOrdering
          && this.selectedLocation.isCurrentServiceAvailable
          && !this.menu.unavailablePerSchedule
          && (this.isFavorite || this.item.quickAddEnabled);
      },

      imageUrl() {
        const image = this.item.images?.find(img => img.label === 'menu');

        return image ? image.url : null;
      },

      itemQuantity() {
        const addedUpsellItem = this.order.orderItems.find(orderItem => orderItem.menuItemId === this.item.menuItemId);

        return addedUpsellItem ? addedUpsellItem.quantity : 0;
      },

      isDescriptionTruncated() {
        const isReadOnlyViewItemEnabled = this.merchant.features['read-only-view-item-details-enabled'];

        const readOnlyViewItemUrlOverride = this.$route.query.dmb === 'true';

        return this.isReadOnly && isReadOnlyViewItemEnabled && !readOnlyViewItemUrlOverride;
      }
    },

    methods: {
      addItemToOrder() {
        if (this.item.ageVerificationTemplate === 'AlcoholTemplate' && !this.session.hasVerifiedAgeForAlcohol) {
          this.$openModal('ageVerificationModal', {
            onConfirm: this.addItem.bind(this),
            required: false
          });
        }
        else {
          this.addItem();
        }
      },

      async addItem() {
        if (this.selectedLocation.storeId) {
          this.isAddingItem = true;

          if (this.isFavorite) {
            await this.$store.dispatch('favoriteItems/addFavoriteItemToOrder', { item: this.item });

            // this.$triggerGoogleTrackingEvent('AddFavoriteItemToCart', 'Favorites');

            this.$triggerNewRelicEvent('AddFavoriteItemToCart', 'Information', 'Added Favorite Item To Cart');
          }

          else {
            const item = this.getItemData();

            await this.$store.dispatch('orders/addItem', { item });

            // this.$triggerGoogleTrackingEvent('AddItemToCart', 'Order');

            this.$triggerNewRelicEvent('AddItemToCart', 'Information', 'Added Item To Cart');
          }

          this.isAddingItem = false;
        }

        else {
          this.openItem();
        }
      },

      openItem() {
        if (this.order.isAddingItem || (this.itemQuantity > 0 && this.isUpsellItem)) {
          return;
        }

        this.$store.dispatch('menu/selectCategory', this.categoryId);

        this.$emit('open-item', this.item);

        const eventData = {
          itemInfo: [{ ...this.item, categoryId: this.categoryId }]
        };

        this.$googleEventTracking.postEvent('view_item', eventData);

        // this.$triggerGoogleTrackingEvent('ViewMenuItem', 'Menu');

        this.$triggerNewRelicEvent('ViewMenuItem', 'Information', 'View Menu Item');
      },

      getItemData() {
        const existingUpsellItem = this.order.orderItems.find(orderItem => orderItem.menuItemId === this.item.menuItemId);

        return this.isUpsellItem && existingUpsellItem
          ? {
            categoryId: this.categoryId,
            name: existingUpsellItem.displayName,
            isUpsell: existingUpsellItem.isUpsell,
            maxAllowed: existingUpsellItem.maxAllowed,
            menuItemId: existingUpsellItem.menuItemId,
            modifiers: existingUpsellItem.modifiers,
            quantity: 1,
            price: existingUpsellItem.price
          }
          : {
            categoryId: this.categoryId,
            name: this.item.displayName,
            isUpsell: this.item.isUpsell,
            maxAllowed: this.item.maxAllowed,
            menuItemId: this.item.menuItemId,
            modifiers: this.getItemModifiers(this.item),
            quantity: 1,
            price: this.item.price
          };
      },

      getItemModifiers(item) {
        if (item.modifierGroups.length) {
          const modifiers = [];

          item.modifierGroups.forEach((group) => {
            group.modifiers.forEach((modifier) => {
              if (modifier.isDefault) {
                modifiers.push({ menuItemId: modifier.menuItemId, quantity: 1 });
              }
            });
          });

          return modifiers;
        }

        return [];
      }
    }
  };
</script>
